import { useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
} from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import { useSession } from '../../app/session';
import { AttachmentList } from '../../components/Attachments';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDate, minutesToHhmm } from '../../lib/format';
import { LogbookView } from '../logbook/LogbookView';
import { CurrencyTab } from './CurrencyTab';
import { PersonFormDrawer } from './PersonFormDrawer';
import { QualificationsTab } from './QualificationsTab';

export function StaffDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { org, personId } = useParams({ from: '/$org/staff/$personId' });
  const search = useSearch({ from: '/$org/staff/$personId' });
  const { can } = useSession();
  const [isFormOpen, setFormOpen] = useState(false);

  const id = Number(personId);
  const tab = search.tab ?? 'overview';
  const setTab = (value: string | null) =>
    void navigate({
      to: '/$org/staff/$personId',
      params: { org, personId },
      search: { tab: value ?? 'overview' },
    });

  const person = useQuery({
    queryKey: ['person', id],
    queryFn: async () => unwrap(await api.GET('/api/v1/people/{id}/', { params: { path: { id } } })),
  });

  const totals = useQuery({
    queryKey: ['person', id, 'totals'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/people/{id}/totals/', { params: { path: { id } } })),
    enabled: tab === 'overview' || tab === 'totals',
  });

  if (person.isLoading) return <Loader />;
  const record = person.data;
  if (!record) return null;

  const profile = record.pilot_profile;

  return (
    <>
      <PageHeader
        title={record.full_name}
        subtitle={record.logbook_name}
        actions={
          can('staff.change') ? (
            <Button
              variant="light"
              leftSection={<IconPencil size={16} />}
              onClick={() => setFormOpen(true)}
            >
              {t('action.edit')}
            </Button>
          ) : null
        }
      />

      <Group gap="sm" mb="md" wrap="wrap">
        <Avatar src={record.photo} size={44} radius="xl">
          {record.first_name[0]}
          {record.last_name[0]}
        </Avatar>
        {profile ? (
          <Badge color={profile.is_student ? 'blue' : 'teal'} variant="light">
            {t(`staff.${profile.status}`)}
          </Badge>
        ) : null}
        {record.is_instructor ? <Badge color="grape">{t('staff.instructor')}</Badge> : null}
        {record.is_examiner ? <Badge color="orange">{t('staff.examiner')}</Badge> : null}
        {profile?.solo_authorised ? (
          <Badge color="teal" variant="outline">
            {t('staff.soloAuthorised')} · {formatDate(profile.solo_authorised_until)}
          </Badge>
        ) : null}
        {record.memberships.map((membership) => (
          <Badge key={membership.organisation} variant="default">
            {membership.organisation_name}
            {membership.membership_number ? ` · ${membership.membership_number}` : ''}
          </Badge>
        ))}
      </Group>

      <Tabs value={tab} onChange={setTab} mb="md">
        <Tabs.List>
          <Tabs.Tab value="overview">{t('nav.profile')}</Tabs.Tab>
          <Tabs.Tab value="qualifications">{t('staff.licences')}</Tabs.Tab>
          <Tabs.Tab value="currency">{t('staff.currency')}</Tabs.Tab>
          <Tabs.Tab value="logbook">{t('staff.logbook')}</Tabs.Tab>
          <Tabs.Tab value="documents">{t('staff.documents')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {tab === 'overview' ? (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Card p="md">
            <Text fw={600} mb="sm">
              {t('staff.person')}
            </Text>
            <Table fz="sm" withRowBorders={false} verticalSpacing={4}>
              <Table.Tbody>
                <DetailRow label={t('staff.dateOfBirth')} value={formatDate(record.date_of_birth)} />
                <DetailRow
                  label={t('staff.nationality')}
                  value={record.nationality ?? undefined}
                />
                <DetailRow label={t('staff.email')} value={record.email ?? undefined} />
                <DetailRow label={t('staff.phone')} value={record.phone ?? undefined} />
                <DetailRow
                  label={t('staff.address')}
                  value={[record.address, record.city, record.postal_code]
                    .filter(Boolean)
                    .join(', ')}
                />
                <DetailRow
                  label={t('staff.emergencyContact')}
                  value={[record.emergency_contact_name, record.emergency_contact_phone]
                    .filter(Boolean)
                    .join(' · ')}
                />
                {profile?.licence_number ? (
                  <DetailRow label={t('staff.licenceNumber')} value={profile.licence_number} />
                ) : null}
              </Table.Tbody>
            </Table>
          </Card>

          <Card p="md">
            <Text fw={600} mb="sm">
              {t('staff.totals')}
            </Text>
            {totals.isLoading ? (
              <Loader size="sm" />
            ) : (totals.data ?? []).length === 0 ? (
              <Text fz="sm" c="dimmed">
                {t('dashboard.noFlightsYet')}
              </Text>
            ) : (
              <Table fz="sm" verticalSpacing={6}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('aircraft.category')}</Table.Th>
                    <Table.Th ta="right">{t('common.total')}</Table.Th>
                    <Table.Th ta="right">{t('logbook.pic')}</Table.Th>
                    <Table.Th ta="right">{t('logbook.dual')}</Table.Th>
                    <Table.Th ta="right">{t('logbook.landings')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(totals.data ?? []).map((row) => (
                    <Table.Tr key={row.id}>
                      <Table.Td>{row.category_label}</Table.Td>
                      <Table.Td ta="right" ff="monospace" fw={600}>
                        {row.total_display}
                      </Table.Td>
                      <Table.Td ta="right" ff="monospace">
                        {row.pic_display}
                      </Table.Td>
                      <Table.Td ta="right" ff="monospace">
                        {row.dual_display}
                      </Table.Td>
                      <Table.Td ta="right" ff="monospace">
                        {(row.landings_day ?? 0) + (row.landings_night ?? 0)}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {profile && (profile.baseline_total_minutes ?? 0) > 0 ? (
              <Text fz="xs" c="dimmed" mt="xs">
                {t('staff.baselineHours')}: {minutesToHhmm(profile.baseline_total_minutes ?? 0)}
              </Text>
            ) : null}
          </Card>

          <Card p="md">
            <Text fw={600} mb="sm">
              {t('staff.medical')}
            </Text>
            {record.medicals.length === 0 ? (
              <Text fz="sm" c="dimmed">
                {t('staff.noMedical')}
              </Text>
            ) : (
              <Stack gap="xs">
                {record.medicals.map((medical) => (
                  <Group key={medical.id} justify="space-between">
                    <Text fz="sm">{medical.medical_class_name}</Text>
                    <Group gap="xs">
                      <Text fz="sm" ff="monospace">
                        {formatDate(medical.valid_until)}
                      </Text>
                      <StatusBadge kind="validity" value={medical.validity_status} size="sm" />
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
          </Card>

          <Card p="md">
            <Text fw={600} mb="sm">
              {t('staff.ratings')}
            </Text>
            {record.ratings.length === 0 ? (
              <Text fz="sm" c="dimmed">
                {t('staff.noRatings')}
              </Text>
            ) : (
              <Group gap="xs" wrap="wrap">
                {record.ratings.map((rating) => (
                  <Badge
                    key={rating.id}
                    variant="light"
                    color={badgeColor(rating.validity_status)}
                  >
                    {rating.rating_code}
                  </Badge>
                ))}
              </Group>
            )}
          </Card>
        </SimpleGrid>
      ) : null}

      {tab === 'qualifications' ? <QualificationsTab personId={id} /> : null}
      {tab === 'currency' ? <CurrencyTab personId={id} /> : null}
      {tab === 'logbook' ? (
        <LogbookView kind="PILOT" subjectId={id} personId={id} title={record.logbook_name} />
      ) : null}
      {tab === 'documents' ? (
        <Card p="md">
          <AttachmentList target="staff.person" targetId={id} canEdit={can('attachments.add')} />
        </Card>
      ) : null}

      <PersonFormDrawer
        opened={isFormOpen}
        onClose={() => setFormOpen(false)}
        person={record}
      />
    </>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <Table.Tr>
      <Table.Td c="dimmed" w={160}>
        {label}
      </Table.Td>
      <Table.Td>{value}</Table.Td>
    </Table.Tr>
  );
}

function badgeColor(status?: string) {
  if (status === 'EXPIRED') return 'red';
  if (status === 'EXPIRING') return 'orange';
  if (status === 'SUSPENDED') return 'gray';
  return 'teal';
}
